import React, { useEffect, useState } from "react";
import { CheckCircle, Download, Calendar as CalendarIcon } from "lucide-react";
import { useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

import { GetEventCheckoutConfirmationDetailsResponseData } from "@/modules/eventRegistration/features/EventRegistration/api/getEventCheckoutConfirmationDetails/types";
import { getEventCheckoutConfirmationDetails } from "@/modules/eventRegistration/features/EventRegistration/api/getEventCheckoutConfirmationDetails/getEventCheckoutConfirmationDetailsApi";
import { getEventCheckoutConfirmationPdf } from "@/modules/eventRegistration/features/EventRegistration/api/getEventCheckoutConfirmationPdf/getEventCheckoutConfirmationPdfApi";

import {
  formatDateTimeRangeInTimeZone,
  formatDateInTimeZone,
  formatLocalTimeRange,
  getLocalTimeZoneShortName,
} from "@/modules/eventRegistration/features/EventDirectory/utils/dateUtils";
import { summarizeAttendeeStatuses } from "@/modules/eventRegistration/features/EventRegistration/utils/summarizeAttendeeStatuses";
import { buildMapsUrl } from "@/modules/eventRegistration/features/EventRegistration/utils/buildMapsUrl";
import { buildIcsFile } from "@/modules/eventRegistration/features/EventRegistration/utils/icsCalendar";

function Confirmation() {
  const { eventCheckoutSessionUuid } = useParams();

  const [details, setDetails] =
    useState<GetEventCheckoutConfirmationDetailsResponseData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!eventCheckoutSessionUuid) {
      setError("No event checkout session uuid found in route.");
      return;
    }

    (async () => {
      try {
        setIsLoading(true);
        const response = await getEventCheckoutConfirmationDetails(
          eventCheckoutSessionUuid,
        );
        setDetails(response.data);
      } catch {
        setError(
          "Failed to load registration details. Please try again later.",
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [eventCheckoutSessionUuid]);

  async function handleDownloadConfirmation() {
    if (!details || !eventCheckoutSessionUuid) return;
    try {
      const { data: pdfBlob } = await getEventCheckoutConfirmationPdf(
        eventCheckoutSessionUuid,
      );

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `confirmation-${details.confirmationNumber || "unknown"}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Failed to download PDF");
    }
  }

  function handleAddToCalendar() {
    if (!details) return;

    const eventTitle = details.eventName ?? "Event";
    const days =
      details.sessionDays && details.sessionDays.length > 0
        ? details.sessionDays
        : [{ id: 0, startDate: details.startDate, endDate: details.endDate }];

    const icsContent = buildIcsFile(eventTitle, days);

    const fileBlob = new Blob([icsContent], { type: "text/calendar" });
    const fileUrl = window.URL.createObjectURL(fileBlob);
    const link = document.createElement("a");
    link.href = fileUrl;
    link.setAttribute("download", "event.ics");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(fileUrl);
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6">
        <div className="flex flex-col items-center gap-4 mb-8">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-40 w-full mb-6" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6">
        <p className="text-gray-500 dark:text-gray-400">
          No confirmation details found.
        </p>
      </div>
    );
  }

  // Derive registration state
  const attendees = details.attendees ?? [];
  const {
    allWaitlisted,
    hasWaitlisted,
    confirmedCount,
    waitlistedCount,
    isMixed,
  } = summarizeAttendeeStatuses(attendees);

  // Three-state copy
  const heroTitle = allWaitlisted
    ? "Added to Waitlist"
    : "Registration Complete";
  const heroSubtitle = allWaitlisted
    ? "You've been added to the waitlist for this event."
    : hasWaitlisted
      ? `${confirmedCount} attendee${confirmedCount !== 1 ? "s" : ""} confirmed. ${waitlistedCount} placed on the waitlist — you'll be notified if a seat opens up.`
      : "Your registration has been successfully processed.";
  // green = all confirmed, amber = all waitlisted, mixed = split badges
  const heroColor = allWaitlisted ? "amber" : "green";

  const paymentStatusText = allWaitlisted ? "Pending confirmation" : "Paid";

  const emailCopyEnd = allWaitlisted
    ? "with all the details about your waitlist status."
    : hasWaitlisted
      ? "with all the details about your registration and waitlist status."
      : "with all the details about your registration.";

  const {
    confirmationNumber,
    amount,
    contactEmail,
    eventName,
    eventSessionName,
    startDate,
    endDate,
    isVirtualOnly,
    venueName,
    venueAddress,
    venueCity,
    venueState,
    venuePostalCode,
    venueCountry,
    timezoneIdentifier,
    timezoneShortName,
    sessionDays,
  } = details;

  // Use session days if available, otherwise fall back to session start/end
  const hasSessionDays = sessionDays && sessionDays.length > 0;

  // Build a timezone-aware date range for the hero
  const eventDateDisplay = (() => {
    if (hasSessionDays) {
      const firstStart = sessionDays[0].startDate;
      const lastEnd =
        sessionDays[sessionDays.length - 1].endDate ??
        sessionDays[sessionDays.length - 1].startDate;
      const firstFormatted = formatDateInTimeZone(
        firstStart,
        timezoneIdentifier,
      );
      const lastFormatted = formatDateInTimeZone(lastEnd, timezoneIdentifier);
      return firstFormatted === lastFormatted
        ? firstFormatted
        : `${firstFormatted} – ${lastFormatted}`;
    }
    if (startDate) {
      const s = formatDateInTimeZone(startDate, timezoneIdentifier);
      if (!endDate) return s;
      const e = formatDateInTimeZone(endDate, timezoneIdentifier);
      return s === e ? s : `${s} – ${e}`;
    }
    return null;
  })();

  const localShortName = getLocalTimeZoneShortName();

  const amountDisplay = amount ? `$${amount}` : "$0.00";

  const mapUrl = buildMapsUrl(
    venueAddress,
    venueCity,
    venueState,
    venuePostalCode,
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
      {/* Actions bar */}
      <div className="flex items-center justify-end py-4 gap-1">
        <Button
          className="text-muted-foreground hover:text-foreground"
          onClick={handleDownloadConfirmation}
          size="sm"
          variant="ghost"
        >
          <Download className="h-4 w-4 mr-1.5" />
          Download
        </Button>
        <Button
          className="text-muted-foreground hover:text-foreground"
          onClick={handleAddToCalendar}
          size="sm"
          variant="ghost"
        >
          <CalendarIcon className="h-4 w-4 mr-1.5" />
          Calendar
        </Button>
      </div>

      {/* Success hero */}
      <div className="flex flex-col items-center text-center py-6">
        <div
          className={`h-12 w-12 rounded-full flex items-center justify-center mb-4 ${
            heroColor === "green"
              ? "bg-green-50 dark:bg-green-900/20"
              : "bg-amber-50 dark:bg-amber-900/20"
          }`}
        >
          <CheckCircle
            className={`h-7 w-7 ${
              heroColor === "green" ? "text-green-500" : "text-amber-500"
            }`}
          />
        </div>
        <h1 className="text-2xl font-normal text-gray-900 dark:text-white mb-2">
          {heroTitle}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mb-4">
          {heroSubtitle}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-gray-400 dark:text-gray-500">
          {confirmationNumber && <span>#{confirmationNumber}</span>}
          {eventDateDisplay && <span>{eventDateDisplay}</span>}
          <span>{amountDisplay}</span>
        </div>
        {/* Status badge(s) */}
        <div className="mt-4 flex items-center gap-2">
          {isMixed ? (
            <>
              <span className="inline-flex items-center text-xs px-2 py-1 rounded-full border border-green-200 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-400 dark:bg-green-900/20">
                {confirmedCount} Confirmed
              </span>
              <span className="inline-flex items-center text-xs px-2 py-1 rounded-full border border-amber-200 text-amber-700 bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:bg-amber-900/20">
                {waitlistedCount} Waitlisted
              </span>
            </>
          ) : (
            <span
              className={`inline-flex items-center text-xs px-2 py-1 rounded-full border ${
                heroColor === "green"
                  ? "border-green-200 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-400 dark:bg-green-900/20"
                  : "border-amber-200 text-amber-700 bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:bg-amber-900/20"
              }`}
            >
              {allWaitlisted ? "Waitlisted" : "Confirmed"}
            </span>
          )}
        </div>
      </div>

      {/* Attendees section */}
      {attendees.length > 0 && (
        <div className="border-t border-gray-200 dark:border-slate-700 pt-6 pb-6">
          <h2 className="text-base font-normal text-gray-900 dark:text-white mb-3">
            Attendees
          </h2>
          <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            {attendees.map((attendee, idx) => (
              <div
                className={`flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 ${
                  idx < attendees.length - 1
                    ? "border-b border-gray-200 dark:border-slate-700"
                    : ""
                }`}
                key={attendee.id}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={`h-2 w-2 rounded-full flex-shrink-0 ${
                      attendee.isWaitlist ? "bg-amber-400" : "bg-green-400"
                    }`}
                  />
                  <span className="text-sm text-gray-900 dark:text-white truncate">
                    {attendee.firstName} {attendee.lastName}
                  </span>
                  {attendee.email && (
                    <span className="text-sm text-gray-400 dark:text-gray-500 truncate hidden sm:inline">
                      {attendee.email}
                    </span>
                  )}
                </div>
                <span
                  className={`text-xs flex-shrink-0 ml-3 ${
                    attendee.isWaitlist
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-green-600 dark:text-green-400"
                  }`}
                >
                  {attendee.isWaitlist ? "Waitlisted" : "Registered"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event details section */}
      <div className="border-t border-gray-200 dark:border-slate-700 pt-6 pb-6">
        <h2 className="text-base font-normal text-gray-900 dark:text-white mb-4">
          Event Details
        </h2>
        <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
          {/* Event */}
          {eventName && (
            <>
              <span className="text-gray-400 dark:text-gray-500">Event</span>
              <span className="text-gray-900 dark:text-white">{eventName}</span>
            </>
          )}

          {/* Session */}
          {eventSessionName && (
            <>
              <span className="text-gray-400 dark:text-gray-500">Session</span>
              <span className="text-gray-900 dark:text-white">
                {eventSessionName}
              </span>
            </>
          )}

          {/* Schedule — session days or fallback */}
          {hasSessionDays ? (
            <>
              <span className="text-gray-400 dark:text-gray-500 pt-0.5">
                Schedule
              </span>
              <div className="space-y-2">
                {sessionDays.map((day) => {
                  const dayRange = formatDateTimeRangeInTimeZone(
                    day.startDate,
                    day.endDate,
                    timezoneIdentifier,
                  );
                  const dayLocal = formatLocalTimeRange(
                    day.startDate,
                    day.endDate,
                  );
                  return (
                    <div key={day.id}>
                      <p className="text-gray-900 dark:text-white">
                        {timezoneShortName || timezoneIdentifier
                          ? `${dayRange} (${timezoneShortName || timezoneIdentifier})`
                          : dayRange || "N/A"}
                      </p>
                      {localShortName &&
                        localShortName !== timezoneShortName && (
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            Local: {dayLocal} ({localShortName})
                          </p>
                        )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : startDate || endDate ? (
            <>
              <span className="text-gray-400 dark:text-gray-500 pt-0.5">
                Schedule
              </span>
              <div>
                <p className="text-gray-900 dark:text-white">
                  {timezoneShortName || timezoneIdentifier
                    ? `${formatDateTimeRangeInTimeZone(startDate, endDate, timezoneIdentifier)} (${timezoneShortName || timezoneIdentifier})`
                    : formatDateTimeRangeInTimeZone(
                        startDate,
                        endDate,
                        timezoneIdentifier,
                      ) || "N/A"}
                </p>
                {localShortName && localShortName !== timezoneShortName && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Local: {formatLocalTimeRange(startDate, endDate)} (
                    {localShortName})
                  </p>
                )}
              </div>
            </>
          ) : null}

          {/* Venue */}
          {isVirtualOnly ? (
            <>
              <span className="text-gray-400 dark:text-gray-500">Location</span>
              <span className="italic text-gray-500 dark:text-gray-400">
                Virtual only
              </span>
            </>
          ) : venueName || venueAddress || venueCity ? (
            <>
              <span className="text-gray-400 dark:text-gray-500 pt-0.5">
                Location
              </span>
              <div>
                {venueName && (
                  <p className="text-gray-900 dark:text-white">{venueName}</p>
                )}
                {(venueAddress || venueCity) && (
                  <a
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white underline transition-colors"
                    href={mapUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {[
                      venueAddress,
                      [venueCity, venueState].filter(Boolean).join(", "),
                      venuePostalCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </a>
                )}
                {venueCountry && venueCountry.trim().length >= 2 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {venueCountry}
                  </p>
                )}
              </div>
            </>
          ) : null}

          {/* Payment */}
          <>
            <span className="text-gray-400 dark:text-gray-500">Payment</span>
            {allWaitlisted ? (
              <span className="italic text-gray-400 dark:text-gray-500">
                {paymentStatusText}
              </span>
            ) : (
              <span className="text-gray-900 dark:text-white">
                {amountDisplay}
              </span>
            )}
          </>
        </div>
      </div>

      {/* Footer note */}
      <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center">
          A confirmation email has been sent to{" "}
          <span className="text-gray-500 dark:text-gray-400">
            {contactEmail || "your email"}
          </span>{" "}
          {emailCopyEnd}
        </p>
      </div>
    </div>
  );
}

export default Confirmation;
